import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import moment from 'moment'

import { FixedSizeList } from 'react-window'

import { isValidPhoneNumber } from 'react-phone-number-input'

import AuthService from '../../../utilities/services/auth.service'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { usePrintTickets } from '../../../providers/PrintTicketsProvider/PrintTicketsProvider'

import { useOrderActions } from '../../../utilities/hooks/useOrderActions'

import { dateTime as SharedDateTime } from '@blocktickets/shared-utils'

import {
  getUserEvents,
  getEventTickets,
  userOrderHistory,
  customerPaymentMethods,
  getBoxOfficeAccountReps,
  getProfileByEmail,
  getProfileByUserId,
  updateProfileByEmail,
} from '../../../utilities/api'

import { constants } from '@blocktickets/shared-utils'

import {
  capitalizeString,
  formatCurrency,
  formatDateTime,
  formatShortDate,
  formatNumber,
  formatPhoneNumber,
  getSinglarOrPluralWord,
  getTimezoneDate,
  sortTickets,
  timezones,
  emailPatternMatch,
  namePatternMatch,
  formatTicketGroups,
  formatString,
  formatSectionRowSeat,
  scrollToTopOfModal,
} from '../../../utilities/helpers'

import {
  Button,
  Modal,
  Form,
  Card,
  Badge,
  Stack,
  Accordion,
  AccordionContext,
  useAccordionButton,
  CloseButton,
  Tabs,
  Tab,
  Table,
  Row,
  Col,
  Alert,
} from 'react-bootstrap'

import { Error as ModalError } from '../Error'
import { Error } from '../../Error'
import { Delete } from '../Delete'
import { Spinner } from '../../LoadingContainer/Spinner'
import { SelectEvent } from '../SelectEvent'
import { TransferTickets } from '../TransferTickets'
import { UpdateBuyerType } from '../UpdateBuyerType'
import { OrderActionsDropdown } from '../../SearchOrdersWrapper/OrdersList/OrderSummary/OrderSummary'
import { PhoneNumberInput } from '../../PhoneNumberInput'
import {
  CustomerPaymentMethodsTable,
  parsePaymentMethodsResponse,
  useCustomerPaymentMethods,
} from '../../CustomerPaymentMethods'
import { DonationsTab } from './DonationsTab'
import { TransferredTicketsTab } from './TransferredTicketsTab'

const { ORDER_SOURCE_LABEL, ORDER_SOURCE, ORDER_STATUS_LABEL, ORDER_STATUS } =
  constants

const TICKET_HEIGHT = 40
const MAX_VISIBLE_TICKETS = 10

function orderHasActivePaymentPlan(order) {
  const plan = order?.payment_plan
  if (!plan) return false
  const plans = Array.isArray(plan) ? plan : [plan]
  return plans.some((entry) => entry && entry.active !== false)
}

function userHasActivePaymentPlan(orders) {
  return Array.isArray(orders) && orders.some(orderHasActivePaymentPlan)
}

function buyerLookup(order) {
  const email = String(
    order?.users_permissions_user?.email || order?.email || ''
  ).trim()
  const numericUserId = Number(
    order?.userId ?? order?.users_permissions_user?.id
  )
  const userId =
    Number.isInteger(numericUserId) && numericUserId > 0
      ? numericUserId
      : undefined
  return {
    email,
    userId: email ? undefined : userId,
    organizationUUID: order?.organizationUUID,
  }
}

export default function ViewProfile({
  order,
  runSearch,
  initialTab = 'tickets',
}) {
  const { closeModal, openModal } = useModal()
  const { handlePrintPDF, preparingPDF, handlePrintThermal, preparingThermal } =
    usePrintTickets()

  const organization = AuthService.getOrg()[0]

  const orderActions = useOrderActions(runSearch)

  const [userEvents, setUserEvents] = useState([])
  const [userOrders, setUserOrders] = useState([])
  const [userProfile, setUserProfile] = useState(null)
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([])
  const [stripeCustomerId, setStripeCustomerId] = useState(null)
  const [eventTickets, setEventTickets] = useState({})
  const [selectedTickets, setSelectedTickets] = useState([])
  const [loadingEvents, setLoadingEvents] = useState({})
  const [activeKey, setActiveKey] = useState(null)
  const [tabKey, setTabKey] = useState(initialTab)

  const [isAnotherModalOpen, setIsAnotherModalOpen] = useState(false)

  // State - use currentOrder so we can update it after profile save
  const [currentOrder, setCurrentOrder] = useState(order || {})

  const [error, setError] = useState(false)
  const [loadingOrder, setLoadingOrder] = useState(true)

  useEffect(() => {
    if (order) setCurrentOrder(order)
  }, [order])

  const handleProfileSaved = useCallback((updatedFields) => {
    setUserProfile((prev) => {
      // When we receive an address object, remove flat address fields so state uses address only
      const { address1, address2, city, state, zipCode, country, ...restPrev } =
        prev || {}
      const hasAddressObject =
        updatedFields?.address && typeof updatedFields.address === 'object'
      const cleanPrev = hasAddressObject ? restPrev : prev
      return { ...cleanPrev, ...updatedFields }
    })
  }, [])

  const userTimezone =
    currentOrder?.event?.venue?.timezone ||
    currentOrder?.package?.venue?.timezone ||
    currentOrder?.flex_pack?.venue?.timezone ||
    'UTC'

  const userFirstName = capitalizeString(
    userProfile?.firstName ||
      currentOrder?.users_permissions_user?.firstName ||
      currentOrder?.firstName ||
      'No'
  )
  const userLastName = capitalizeString(
    userProfile?.lastName ||
      currentOrder?.users_permissions_user?.lastName ||
      currentOrder?.lastName ||
      'User'
  )
  const userEmail =
    userProfile?.email ||
    userProfile?.username ||
    currentOrder?.users_permissions_user?.email ||
    currentOrder?.email ||
    ''

  const userPhoneNumber =
    userProfile?.phoneNumber ||
    currentOrder?.phoneNumber ||
    currentOrder?.users_permissions_user?.phoneNumber ||
    ''

  const userProfileOrganizationName =
    userProfile?.organizationName ||
    userProfile?.organization ||
    currentOrder?.purchasedByOrganizationName ||
    ''

  const userProfileSalesRep =
    userProfile?.sales_rep?.id?.toString() ??
    currentOrder?.boxOfficeAgent?.id?.toString() ??
    ''

  const userProfileSalesRepName = userProfile?.sales_rep
    ? capitalizeString(
        `${userProfile?.sales_rep?.firstName} ${userProfile?.sales_rep?.lastName}`
      )
    : currentOrder?.boxOfficeAgent
      ? capitalizeString(
          `${currentOrder?.boxOfficeAgent?.firstName} ${currentOrder?.boxOfficeAgent?.lastName}`
        )
      : ''

  const connectedAccountId =
    process.env.REACT_APP_STAGE === 'production'
      ? organization.connected_account_id
      : organization.test_connected_account_id

  const preventLastPaymentMethod = useMemo(
    () => userHasActivePaymentPlan(userOrders),
    [userOrders]
  )

  const reloadPaymentMethods = useCallback(async () => {
    if (!userEmail || !connectedAccountId) {
      setSavedPaymentMethods([])
      setStripeCustomerId(null)
      return
    }

    try {
      const savedCardsRes = await customerPaymentMethods(
        connectedAccountId,
        userEmail
      )
      const { paymentMethods, stripeCustomerId: nextStripeCustomerId } =
        parsePaymentMethodsResponse(savedCardsRes)
      setSavedPaymentMethods(paymentMethods)
      setStripeCustomerId(nextStripeCustomerId)
    } catch (paymentMethodsError) {
      console.warn(
        'Unable to load customer payment methods:',
        paymentMethodsError
      )
      setSavedPaymentMethods([])
      setStripeCustomerId(null)
    }
  }, [connectedAccountId, userEmail])

  const loadUserEvents = async () => {
    if (!currentOrder) return

    const lookup = buyerLookup(currentOrder)
    setLoadingOrder(true)
    try {
      const apiQuery = {
        email: lookup.email,
        userId: lookup.userId,
        organizationUUID: lookup.organizationUUID,
      }

      const res = await getUserEvents(apiQuery)
      setUserEvents(res.data.events || [])
      try {
        const profileRes = lookup.userId
          ? await getProfileByUserId(lookup.userId, lookup.organizationUUID)
          : await getProfileByEmail(
              lookup.email || userEmail,
              lookup.organizationUUID
            )
        const profile = profileRes?.data ?? null
        setUserProfile(profile)
      } catch (profileErr) {
        console.warn('Unable to load user profile:', profileErr)
        setUserProfile(null)
      }
      const ordersRes = await userOrderHistory(apiQuery)
      const ordersData = ordersRes?.data?.orders || ordersRes?.data || []
      setUserOrders(Array.isArray(ordersData) ? ordersData : [])
      await reloadPaymentMethods()
    } catch (err) {
      console.error('Error loading user profile:', err)
      setError(true)
    } finally {
      setLoadingOrder(false)
    }
  }

  // Load user events on mount
  useEffect(() => {
    loadUserEvents()
  }, [])

  // Load tickets for a specific event
  const loadEventTickets = useCallback(
    async (eventUuid) => {
      if (eventTickets[eventUuid] || loadingEvents[eventUuid]) return

      setLoadingEvents((prev) => ({ ...prev, [eventUuid]: true }))

      try {
        const lookup = buyerLookup(currentOrder)
        const apiQuery = {
          email: lookup.userId ? '' : lookup.email || userEmail,
          userId: lookup.userId,
          organizationUUID: lookup.organizationUUID || order.organizationUUID,
          eventUuid: eventUuid,
        }

        const res = await getEventTickets(apiQuery)
        const tickets = res.data.tickets || []

        setEventTickets({ [eventUuid]: tickets }) // Just store one at a time ( to keep memory light )
      } catch (err) {
        console.error(`Error loading tickets for event ${eventUuid}:`, err)
      } finally {
        setLoadingEvents((prev) => ({ ...prev, [eventUuid]: false }))
      }
    },
    [eventTickets, loadingEvents, currentOrder, userEmail]
  )

  // Handle accordion toggle
  const handleAccordionSelect = useCallback(
    (eventKey) => {
      setActiveKey(eventKey)

      // Load tickets when opening an accordion item
      if (eventKey) {
        loadEventTickets(eventKey)
      }
    },
    [loadEventTickets]
  )

  const getSelectedTicketCount = () => {
    return selectedTickets.length
  }

  const handleTicketSelect = useCallback((ticketToToggle) => {
    setSelectedTickets((prevSelected) => {
      const isSelected = prevSelected.some(
        (ticket) => ticket.id === ticketToToggle.id
      )
      if (isSelected) {
        return prevSelected.filter((ticket) => ticket.id !== ticketToToggle.id)
      } else {
        return [...prevSelected, ticketToToggle]
      }
    })
  }, [])

  const handleEventSelectToggle = useCallback(
    (eventUuid) => {
      const ticketsInEvent = eventTickets[eventUuid] || []
      const validTickets = ticketsInEvent?.filter((t) => !t.exchanged)
      const eventTicketIds = validTickets.map((t) => t.id)
      const areAllSelected = eventTicketIds.every((id) =>
        selectedTickets.some((selected) => selected.id === id)
      )

      if (areAllSelected) {
        setSelectedTickets((prev) =>
          prev.filter((ticket) => !eventTicketIds.includes(ticket.id))
        )
      } else {
        const newTicketsToAdd = validTickets.filter(
          (eventTicket) =>
            !selectedTickets.some((st) => st.id === eventTicket.id)
        )
        setSelectedTickets((prev) => [...prev, ...newTicketsToAdd])
      }
    },
    [selectedTickets, eventTickets]
  )

  const getEventCheckboxState = useCallback(
    (eventUuid) => {
      const ticketsInEvent = eventTickets[eventUuid] || []
      const ticketsInEventIds = ticketsInEvent.map((t) => t.id)
      const selectedInEventCount = selectedTickets.filter((selectedTicket) =>
        ticketsInEventIds.includes(selectedTicket.id)
      ).length

      if (ticketsInEvent.length === 0) return false
      if (selectedInEventCount === ticketsInEventIds.length) return true
      if (
        selectedInEventCount > 0 &&
        selectedInEventCount < ticketsInEventIds.length
      ) {
        return 'indeterminate'
      }
      return false
    },
    [selectedTickets, eventTickets]
  )

  const getNameInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0)}${lastName?.charAt(0)}`
  }

  const clickPrintPDF = async () => {
    const event = userEvents.find((event) => event.uuid === activeKey)
    const events = event ? [event] : []
    await handlePrintPDF(
      events,
      selectedTickets,
      userEmail,
      currentOrder?.purchasedByOrganizationName
    )
  }

  const clickPrintThermal = async () => {
    const event = userEvents.find((event) => event.uuid === activeKey)
    const events = event ? [event] : []
    await handlePrintThermal(events, selectedTickets)
  }

  const openSelectEventModal = () => {
    openModal(
      SelectEvent,
      {
        order,
        event: userEvents.find((event) => event.uuid === activeKey),
        selectedTickets,
        onClose: closeModal,
      },
      { className: 'vjx-modal-2xl', contentClassName: '', scrollable: true }
    )
  }

  const openTransferTicketsModal = () => {
    openModal(
      TransferTickets,
      {
        order,
        fromEmail: userEmail,
        selectedTickets,
      },
      { className: 'vjx-modal-2xl', contentClassName: '', scrollable: true }
    )
  }

  // Ticket row component for the virtualized list
  const TicketRow = useCallback(
    ({ index, style, data }) => {
      const ticket = data[index]
      console.log('ticket', ticket)

      return (
        <div style={style}>
          <div
            className="d-flex align-items-center px-3 border-top"
            style={{
              height: '100%',
            }}
          >
            <Form.Check
              type="checkbox"
              className="form-check-display m-0"
              id={`ticket-${ticket.id}`}
            >
              <Form.Check.Input
                type="checkbox"
                checked={selectedTickets.some(
                  (selectedTicket) => selectedTicket.id === ticket.id
                )}
                onChange={() => handleTicketSelect(ticket)}
                disabled={ticket.exchanged}
              />
              <Stack direction="horizontal" gap={3} className="flex-grow-1">
                <Form.Check.Label
                  id={`ticket-${ticket.id}`}
                  className="m-0 d-flex gap-3 w-100 h-100 align-items-center form-check-label-sm"
                >
                  {formatSectionRowSeat(ticket)}
                </Form.Check.Label>
              </Stack>
            </Form.Check>

            <Stack direction="horizontal" gap={2}>
              {ticket?.orderStatus === 'pending_send' && (
                <Badge bg="none" className="badge-sm badge--pending">
                  Unsent
                </Badge>
              )}
              {ticket?.wasPrinted && (
                <Badge bg="none" className="badge-sm badge--printed">
                  Printed
                </Badge>
              )}
              {ticket?.wasScanned && (
                <Badge bg="none" className="badge-sm badge--scanned">
                  Scanned In
                </Badge>
              )}
              {ticket?.exchanged && (
                <Badge bg="none" className="badge-sm badge--exchanged">
                  Exchanged
                </Badge>
              )}
            </Stack>
          </div>
        </div>
      )
    },
    [selectedTickets, handleTicketSelect]
  )

  if (loadingOrder) {
    return (
      <div className="d-flex h-100 justify-content-center align-items-center">
        <div className="text-center">
          <Spinner variant="dark" size="lg">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <div className="mt-3">Loading profile...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ paddingTop: 32 }}>
        <ModalError
          title="Error loading profile!"
          description={null}
          onClose={closeModal}
        />
      </div>
    )
  }

  const handleCloseModal = () => {
    setIsAnotherModalOpen(false)
    closeModal()
  }

  return (
    <div
      className={`h-100 d-flex d-flex-column ${isAnotherModalOpen ? 'modal-opac' : ''}`}
      style={{ overflow: 'hidden' }}
    >
      <Modal.Header className="border-0 pb-0 mb-4 d-flex-column gap-2">
        <Stack direction="horizontal" gap={3} className="align-items-end">
          <CloseButton onClick={closeModal} />
        </Stack>
        <Stack
          direction="horizontal"
          className="align-items-start justify-content-between"
        >
          <Stack
            direction="horizontal"
            gap={3}
            className="align-items-start flex-fill"
          >
            <div className={`user-profile user-profile text-muted`}>
              <span>
                {getNameInitials(userFirstName, userLastName).toUpperCase()}
              </span>
            </div>
            <Stack>
              <Stack direction="horizontal" gap={2}>
                <Modal.Title as="h4" className="text-dark">
                  {capitalizeString(`${userFirstName} ${userLastName}`)}
                </Modal.Title>
                {userProfileOrganizationName && (
                  <span className="normal normal-bold border-left ps-2">
                    {userProfileOrganizationName}
                  </span>
                )}
                {userProfile && userOrders?.some((o) => o?.package) && (
                  <span className="caption border-left ps-2">
                    Member since{' '}
                    {formatShortDate(
                      userProfile?.memberSince
                        ? moment(userProfile.memberSince)
                        : getTimezoneDate(userProfile?.createdAt, userTimezone),
                      'monthYearOnly'
                    )}
                  </span>
                )}
              </Stack>
              <Stack
                direction="horizontal"
                gap={3}
                className="align-items-center"
              >
                <span className="d-block small">{userEmail || 'No email'}</span>
                <span className="d-block small">
                  {formatPhoneNumber(userPhoneNumber)}
                </span>
              </Stack>
            </Stack>
          </Stack>
          {userProfileSalesRepName && (
            <Stack className="flex-fill align-items-end" gap={1}>
              <span className="small m-0 text-muted">Sales Rep</span>
              <span className="normal normal-bold">
                {userProfileSalesRepName}
              </span>
            </Stack>
          )}
        </Stack>
      </Modal.Header>
      <Modal.Body className="py-3 px-4 pt-0-important" id="view-profile-modal">
        <Tabs
          id="user-profile-tabs"
          variant="pills"
          activeKey={tabKey}
          onSelect={(k) => setTabKey(k)}
          className="mb-3 nav-pills-default position-sticky top-0 z-1 w-max-content"
        >
          <Tab eventKey="tickets" title="All Tickets">
            {userEvents.length === 0 ? (
              <div className="text-center py-5 text-muted">
                No tickets found for this user.
              </div>
            ) : (
              <Accordion activeKey={activeKey} onSelect={handleAccordionSelect}>
                {userEvents.map((event) => {
                  const eventCheckboxState = getEventCheckboxState(event.uuid)
                  const tickets = sortTickets(eventTickets[event.uuid] || [])
                  const isLoading = loadingEvents[event.uuid]

                  return (
                    <Card
                      key={event.uuid}
                      className="card-with-border mb-3 br-md"
                      style={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                      }}
                    >
                      <Card.Header className="p-0">
                        <EventToggle
                          eventKey={event.uuid}
                          event={event}
                          eventCheckboxState={eventCheckboxState}
                          handleEventSelectToggle={handleEventSelectToggle}
                          callback={handleAccordionSelect}
                          loading={isLoading}
                          isAllTicketsDisabled={tickets.every(
                            (ticket) => ticket?.exchanged
                          )}
                        />
                      </Card.Header>
                      <Accordion.Collapse eventKey={event.uuid}>
                        <Card.Body className="p-0">
                          {isLoading ? (
                            <div className="d-flex justify-content-center align-items-center py-3 gap-2">
                              <Spinner variant="dark" size="sm" />
                              <span className="text-muted">
                                Loading tickets...
                              </span>
                            </div>
                          ) : tickets.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                              No tickets found for this event
                            </div>
                          ) : (
                            <Card>
                              <Card.Body className="p-0">
                                <FixedSizeList
                                  height={
                                    Math.min(
                                      tickets.length,
                                      MAX_VISIBLE_TICKETS
                                    ) * TICKET_HEIGHT
                                  }
                                  itemCount={tickets.length}
                                  itemSize={TICKET_HEIGHT}
                                  itemData={tickets}
                                >
                                  {TicketRow}
                                </FixedSizeList>
                              </Card.Body>
                            </Card>
                          )}
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  )
                })}
              </Accordion>
            )}
          </Tab>
          <Tab eventKey="transferred" title="Transferred">
            <TransferredTicketsTab
              email={userEmail}
              userId={currentOrder?.users_permissions_user?.id}
              organizationUUID={
                organization?.uuid || currentOrder?.organizationUUID
              }
              active={tabKey === 'transferred'}
            />
          </Tab>
          <Tab eventKey="orders" title="Orders">
            <OrdersTable
              formatSectionRowSeat={formatSectionRowSeat}
              order={currentOrder}
              orders={userOrders}
              setOrders={setUserOrders}
              orderActions={orderActions}
              runSearch={runSearch}
              setIsAnotherModalOpen={setIsAnotherModalOpen}
              organization={organization}
              openModal={openModal}
            />
          </Tab>
          <Tab eventKey="donations" title="Donations">
            <DonationsTab
              fanEmail={userProfile?.email || currentOrder?.email}
              organizationUUID={organization?.uuid}
              fanProfile={{
                firstName: userFirstName,
                lastName: userLastName,
                email: userEmail,
                phoneNumber: userPhoneNumber,
              }}
              organization={organization}
            />
          </Tab>
          {/* <Tab eventKey="Billing" title="Billing">
            <BillingTable
              order={currentOrder}
              orders={userOrders}
              orderActions={orderActions}
            />
          </Tab> */}
          <Tab eventKey="payment" title="Payment Methods">
            <PaymentMethods
              connectAccountId={connectedAccountId}
              stripeCustomerId={stripeCustomerId}
              savedCards={savedPaymentMethods}
              reloadPaymentMethods={reloadPaymentMethods}
              preventLastPaymentMethod={preventLastPaymentMethod}
              openModal={openModal}
              closeModal={closeModal}
              handleCloseModal={handleCloseModal}
              setIsAnotherModalOpen={setIsAnotherModalOpen}
            />
          </Tab>
          <Tab eventKey="profile" title="Profile Information">
            <UserProfile
              organization={organization}
              order={currentOrder}
              profile={userProfile}
              userOrganizationName={userProfileOrganizationName}
              userFirstName={userFirstName}
              userLastName={userLastName}
              userEmail={userEmail}
              userPhoneNumber={userPhoneNumber}
              userSalesRep={userProfileSalesRep}
              userSalesRepName={userProfileSalesRepName}
              tabKey={tabKey}
              onProfileSaved={handleProfileSaved}
              userTimezone={userTimezone}
            />
          </Tab>
          <Tab eventKey="notes" title="Notes">
            <UserNotes
              profile={userProfile}
              userSalesRepName={userProfileSalesRepName}
              userEmail={userEmail}
              tabKey={tabKey}
              onNoteSaved={handleProfileSaved}
              openModal={openModal}
              handleCloseModal={handleCloseModal}
              setIsAnotherModalOpen={setIsAnotherModalOpen}
              userTimezone={userTimezone}
            />
          </Tab>
        </Tabs>
      </Modal.Body>
      {tabKey === 'tickets' && (
        <Modal.Footer className="border-0 pt-0 mt-4 px-4 d-flex flex-row  w-100">
          <div className="d-flex align-items-center gap-3">
            <span className="small">
              <span>
                {formatNumber(getSelectedTicketCount())}{' '}
                {getSinglarOrPluralWord(getSelectedTicketCount()).toLowerCase()}
              </span>{' '}
              selected
            </span>
            <Button
              variant="outline-light"
              disabled={getSelectedTicketCount() === 0}
              onClick={openSelectEventModal}
              className="btn-refresh icon-button-sm"
            >
              Exchange tickets
            </Button>
            <Button
              variant="outline-light"
              disabled={getSelectedTicketCount() === 0}
              onClick={openTransferTicketsModal}
              className="btn-refresh icon-button-sm"
            >
              Transfer tickets
            </Button>
            <Button
              variant="primary"
              disabled={getSelectedTicketCount() === 0 || preparingPDF}
              onClick={clickPrintPDF}
              style={{ minWidth: '100px' }}
            >
              {preparingPDF ? (
                <Spinner variant="white" size="md" />
              ) : (
                'Print PDF'
              )}
            </Button>
            <Button
              variant="primary"
              disabled={getSelectedTicketCount() === 0 || preparingThermal}
              onClick={clickPrintThermal}
            >
              {preparingThermal ? (
                <Spinner variant="white" size="md" />
              ) : (
                'Print Thermal'
              )}
            </Button>
          </div>
        </Modal.Footer>
      )}
    </div>
  )
}

function EventToggle({
  eventKey,
  event,
  eventCheckboxState,
  handleEventSelectToggle,
  callback,
  loading,
  isAllTicketsDisabled,
}) {
  const { activeEventKey } = useContext(AccordionContext)
  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => callback && callback(eventKey)
  )
  const isCurrentEventKey = activeEventKey === eventKey

  return (
    <div
      className="d-flex align-items-center justify-content-between w-100 p-3"
      style={{ cursor: 'pointer' }}
      onClick={decoratedOnClick}
    >
      <div className="d-flex flex-row align-items-start">
        <Form.Check
          type="checkbox"
          id={`event-select-${event.uuid}`}
          checked={eventCheckboxState === true}
          ref={(input) => {
            if (input) {
              input.indeterminate = eventCheckboxState === 'indeterminate'
            }
          }}
          onChange={(e) => {
            e.stopPropagation()
            handleEventSelectToggle(event.uuid)
          }}
          className="me-3"
          onClick={(e) => e.stopPropagation()}
          disabled={loading || isAllTicketsDisabled || !isCurrentEventKey}
        />

        <div className="flex-grow-1">
          <div
            className="mb-1"
            style={{
              color: '#141416',
              fontWeight: 500,
              fontFamily: 'Poppins',
              fontSize: '14px',
              lineHeight: '24px',
            }}
          >
            {event.name}
          </div>
          <div
            className="small"
            style={{
              color: '#777E90',
              fontFamily: 'Poppins',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '170%',
            }}
          >
            {`${formatDateTime(getTimezoneDate(event.start, event?.timezone))}`}
          </div>
        </div>
      </div>
      <div>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          style={{
            transform: isCurrentEventKey ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="#777E90"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  )
}

function OrdersTable({
  order,
  orders,
  setOrders,
  orderActions,
  runSearch,
  formatSectionRowSeat,
  setIsAnotherModalOpen,
  organization,
  openModal,
}) {
  const [locationModalSeats, setLocationModalSeats] = useState(null)

  // buyer type is editable if the order
  // - is a ticket assignment and
  // - the order is not refunded or released
  // - the order is not reserved
  const isBuyerTypeEditable = (order) => {
    return (
      order.source === ORDER_SOURCE.TICKET_ASSIGNMENT &&
      order?.status !== ORDER_STATUS.REFUNDED &&
      order?.status !== ORDER_STATUS.RELEASED &&
      order?.status !== ORDER_STATUS.RESERVED
    )
  }

  const handleUpdateBuyerType = (orderItem, buyerType) => {
    setIsAnotherModalOpen(true)
    openModal(
      UpdateBuyerType,
      {
        order: orderItem,
        organizationUUID: organization.uuid,
        currentBuyerType: buyerType,
        setOrders,
        onClose: () => setIsAnotherModalOpen(false),
      },
      {
        className: 'modal--sm',
        contentClassName: '',
        scrollable: true,
      },
      { replace: false }
    )
  }

  return (
    <div className="table-container">
      <div className="table-container--fixed-height--scrollable">
        <Table responsive>
          <thead>
            <tr className="with-inside-borders">
              <th>Order ID</th>
              <th className="text-left">Order Date</th>
              <th className="text-left">Event</th>
              <th className="text-left">Product Type</th>
              <th className="text-left w-content">Ticket Quantity</th>
              <th className="text-left">Location</th>
              <th className="text-left">Buyer Type</th>
              <th className="text-left">Source</th>
              <th className="text-left">Gross</th>
              <th className="text-left">Payment Method</th>
              <th className="text-left">Status</th>
              <th className="w-content"></th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr className="with-inside-borders">
                <td colSpan={13} className="text-center text-muted py-4">
                  No orders found for this user.
                </td>
              </tr>
            ) : (
              orders.map((orderItem) => {
                const event = orderItem?.event
                const packageItem = orderItem?.package
                const flexPack = orderItem?.flex_pack
                const ticketsSource =
                  orderItem?.originalTickets || orderItem?.tickets || []
                const canEditBuyerType = isBuyerTypeEditable(orderItem)
                const formattedTickets = formatTicketGroups(
                  packageItem ? orderItem?.package_tickets : ticketsSource
                )
                const buyerType = orderItem?.buyer_type
                const orderDate =
                  orderItem?.processedAt || orderItem?.processed_at
                const getVenueTimezone = (venue) => venue?.timezone
                const venueTimezone =
                  getVenueTimezone(event?.venue) ||
                  getVenueTimezone(packageItem?.venue) ||
                  getVenueTimezone(flexPack?.venue)
                const timezone = timezones(venueTimezone || orderItem?.timezone)

                return (
                  <tr
                    key={orderItem?.orderId || orderItem?.id}
                    className="with-inside-borders"
                  >
                    <td>{orderItem?.orderId}</td>
                    <td className="text-left">
                      {formatDateTime(getTimezoneDate(orderDate, timezone))}
                    </td>
                    <td className="text-left">
                      {event?.name ||
                        packageItem?.name ||
                        flexPack?.name ||
                        'N/A'}
                    </td>
                    <td className="text-left">
                      {event
                        ? 'Event'
                        : flexPack
                          ? 'Flex Package'
                          : 'Season Package'}
                    </td>
                    <td className="text-left">
                      {formatNumber(
                        ticketsSource?.length || flexPack?.gameTickets || 0
                      )}
                    </td>
                    <td className="text-left">
                      {flexPack ? (
                        <Button
                          variant="link"
                          className="p-0 ms-1 text-primary text-decoration-underline"
                          onClick={() => {
                            setIsAnotherModalOpen(true)
                            orderActions.openViewVouchers(
                              orderItem?.vouchers,
                              () => setIsAnotherModalOpen(false),
                              false
                            )
                          }}
                        >
                          View codes
                        </Button>
                      ) : (
                        <>
                          {formattedTickets?.length > 0 ? (
                            <>
                              {formattedTickets?.length > 1 ? (
                                <Button
                                  variant="link"
                                  className="p-0 ms-1 text-primary text-decoration-underline"
                                  onClick={() => {
                                    setIsAnotherModalOpen(true)
                                    setLocationModalSeats(
                                      formattedTickets.map((g) =>
                                        formatSectionRowSeat(g)
                                      )
                                    )
                                  }}
                                >
                                  View locations
                                </Button>
                              ) : (
                                formatSectionRowSeat(formattedTickets[0])
                              )}
                            </>
                          ) : (
                            'N/A'
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {canEditBuyerType ? (
                        <Button
                          variant="link"
                          className="badge badge-sm badge-label badge-label-sm w-max-content text-body hover-effect"
                          onClick={() =>
                            handleUpdateBuyerType(orderItem, buyerType)
                          }
                        >
                          {buyerType?.name || buyerType?.abbreviation || 'N/A'}
                        </Button>
                      ) : (
                        <Badge
                          bg="none"
                          className="badge badge-sm badge-label badge-label-sm w-max-content"
                        >
                          {buyerType?.name || buyerType?.abbreviation || 'N/A'}
                        </Badge>
                      )}
                    </td>
                    <td className="text-left">
                      {ORDER_SOURCE_LABEL[orderItem?.source] ||
                        formatString(orderItem?.source) ||
                        'N/A'}
                    </td>
                    <td className="text-left">
                      {formatCurrency(orderItem?.gross)}
                    </td>
                    <td className="text-left">
                      {formatString(orderItem?.paymentMethodType) || 'N/A'}
                    </td>
                    <td>
                      <Badge
                        bg="none"
                        className={`badge-sm badge--${orderItem?.status} w-max-content`}
                      >
                        {ORDER_STATUS_LABEL[orderItem?.status] ||
                          formatString(orderItem?.status) ||
                          'N/A'}
                      </Badge>
                    </td>
                    <td className="d-flex justify-content-center">
                      <OrderActionsDropdown
                        orderActions={orderActions}
                        order={orderItem}
                        runSearch={runSearch}
                        isViewingProfile={true}
                        isFlexPack={Boolean(flexPack)}
                        popperStrategy="fixed"
                      />
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </div>

      <Modal
        show={!!locationModalSeats}
        onHide={() => {
          setIsAnotherModalOpen(false)
          setLocationModalSeats(null)
        }}
        centered
        className="vjx-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title as="h4" className="fw-semibold">
            Location
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-1">
          <div className="text-muted small mb-3">
            Full breakdown of seating locations
          </div>
          <div style={{ whiteSpace: 'pre-line' }}>
            {!locationModalSeats?.length ? (
              <h1 className="fs-md text-center text-muted py-3">
                No locations found
              </h1>
            ) : (
              <Stack as="ul" gap={1}>
                {locationModalSeats?.map((seat, idx) => (
                  <li key={idx} className="fw-medium">
                    {seat}
                  </li>
                ))}
              </Stack>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

function BillingTable({ order, orders, orderActions }) {
  const ordersToShow =
    Array.isArray(orders) && orders.length > 0 ? orders : order ? [order] : []

  const isBillingOrder = (orderItem) =>
    Boolean(orderItem?.payment_plan?.length > 0)

  const billingOrders = ordersToShow.filter(isBillingOrder)

  console.log('billingOrders: ', billingOrders)

  return (
    <div className="table-container">
      <div className="table-container--fixed-height--scrollable">
        <Table responsive>
          <thead>
            <tr className="with-inside-borders">
              <th>Product Name</th>
              <th className="text-left">Amount</th>
              <th className="text-left">Sales Channel</th>
              <th className="text-left">Payment Schedule</th>
              <th className="text-left">Payment Option</th>
              <th className="text-left">Payment Method</th>
              <th className="text-left">Installments</th>
              <th className="text-left">Payment Date</th>
              <th className="text-left">Payment On</th>
              <th className="text-left">Status</th>
              <th className="w-content"></th>
            </tr>
          </thead>
          <tbody>
            {billingOrders.length === 0 ? (
              <tr className="with-inside-borders">
                <td colSpan={12} className="text-center text-muted py-4">
                  No payment plan orders found for this user.
                </td>
              </tr>
            ) : (
              billingOrders.map((orderItem) => {
                const event = orderItem?.event
                const packageItem = orderItem?.package
                const flexPack = orderItem?.flex_pack
                const getVenueTimezone = (venue) => venue?.timezone
                const venueTimezone =
                  getVenueTimezone(event?.venue) ||
                  getVenueTimezone(packageItem?.venue) ||
                  getVenueTimezone(flexPack?.venue)
                const tz = timezones(venueTimezone || orderItem?.timezone)
                const paymentPlan = orderItem?.payment_plan
                const installments = paymentPlan?.installments || []
                const paidInstallments = installments.filter(
                  (installment) =>
                    String(installment?.status).toLowerCase() === 'paid'
                ).length
                const totalInstallments = installments.length
                const paymentStatus =
                  orderItem?.payment_status ||
                  orderItem?.paymentStatus ||
                  orderItem?.status
                const paymentDate =
                  orderItem?.paymentCompletedOn ||
                  orderItem?.datePaid ||
                  orderItem?.processedAt ||
                  orderItem?.processed_at
                const paymentOn =
                  paymentPlan?.next_charge_at ||
                  orderItem?.dueDate ||
                  orderItem?.invoiceCompleteOn
                const paymentMethod = formatString(
                  orderItem?.payment_method_type || orderItem?.last_4
                )
                const flexPackTicketCount =
                  flexPack?.gameTickets ||
                  flexPack?.game_tickets ||
                  orderItem?.tickets?.length ||
                  0
                const productDetails = event
                  ? '1 event'
                  : flexPack
                    ? `${flexPackTicketCount} ${getSinglarOrPluralWord(
                        flexPackTicketCount,
                        'Voucher'
                      )}`
                    : `${packageItem?.events?.length || 0} ${getSinglarOrPluralWord(
                        packageItem?.events?.length || 0,
                        'Event'
                      )}`
                const paymentSchedule =
                  paymentPlan || String(paymentStatus).toLowerCase() === 'plan'
                    ? 'Payment Plan'
                    : 'One-time'
                const isAssignment =
                  orderItem?.source === 'ticket_assignment' &&
                  orderItem?.assignmentType !== 'comp'

                return (
                  <tr
                    key={orderItem?.orderId || orderItem?.id}
                    className="with-inside-borders"
                  >
                    <td>{event?.name || packageItem?.name || 'N/A'}</td>
                    <td className="text-left">
                      {formatCurrency(orderItem?.total)}
                    </td>
                    <td className="text-left">
                      {ORDER_SOURCE_LABEL[orderItem?.source] ||
                        formatString(orderItem?.source) ||
                        'N/A'}
                    </td>
                    <td className="text-left">{paymentSchedule}</td>
                    <td className="text-left">
                      {formatString(
                        orderItem?.paymentOption || orderItem?.payment_option
                      ) || 'N/A'}
                    </td>
                    <td className="text-left">{paymentMethod || 'N/A'}</td>
                    <td className="text-left">
                      {totalInstallments > 0
                        ? `${paidInstallments} of ${totalInstallments}`
                        : 'N/A'}
                    </td>
                    <td className="text-left">
                      {paymentDate
                        ? formatDateTime(
                            getTimezoneDate(paymentDate, tz),
                            'dateOnly'
                          )
                        : 'N/A'}
                    </td>
                    <td className="text-left">
                      {paymentOn
                        ? formatDateTime(
                            getTimezoneDate(paymentOn, tz),
                            'dateOnly'
                          )
                        : 'N/A'}
                    </td>
                    <td>
                      <Badge
                        bg="none"
                        className={`badge-sm badge--primary w-max-content`}
                      >
                        {formatString(paymentStatus) || 'N/A'}
                      </Badge>
                    </td>
                    <td className="d-flex justify-content-center">
                      <OrderActionsDropdown
                        orderActions={orderActions}
                        order={orderItem}
                        showOptions={[3, 7, 6, isAssignment && 8]}
                        popperStrategy="fixed"
                        isFlexPack={Boolean(flexPack)}
                      />
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  )
}

/** Extract plain text from profile note component (Strapi blocks) for display in textarea */
function notesToPlainText(noteComponent) {
  if (!noteComponent?.note) return ''
  const blocks = Array.isArray(noteComponent.note) ? noteComponent.note : []
  return blocks
    .map((block) => {
      if (!block?.children) return ''
      return block.children
        .filter((c) => c?.text != null)
        .map((c) => c.text)
        .join('')
    })
    .join('\n')
}

function UserNotes({
  profile,
  userSalesRepName,
  userEmail,
  tabKey,
  onNoteSaved,
  openModal,
  handleCloseModal,
  setIsAnotherModalOpen,
  userTimezone,
}) {
  const [noteValue, setNoteValue] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [isModified, setIsModified] = useState(false)
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [alert, setAlert] = useState({ show: false, variant: '', message: '' })
  const prevTabKeyRef = useRef(null)

  const hasNotes = !!(notesToPlainText(profile?.note) ?? '').trim()
  const showForm = (hasNotes && isEditing) || isAddingNote

  const savedAtFormatted = hasNotes
    ? formatShortDate(getTimezoneDate(new Date(), userTimezone), 'monthDay')
    : ''

  const closeAlert = () => setAlert({ show: false, variant: '', message: '' })

  // Sync noteValue when switching to the notes tab; close alert when entering
  useEffect(() => {
    const justEnteredNotes =
      prevTabKeyRef.current !== 'notes' && tabKey === 'notes'
    prevTabKeyRef.current = tabKey
    if (tabKey === 'notes') {
      setNoteValue(profile ? (notesToPlainText(profile?.note) ?? '') : '')
      if (justEnteredNotes) {
        closeAlert()
      }
    } else {
      setIsAddingNote(false)
      setIsEditing(false)
    }
  }, [tabKey, profile])

  // Check if note has changed from original (profile)
  useEffect(() => {
    const initial = profile ? (notesToPlainText(profile?.note) ?? '') : ''
    const hasChanged = noteValue !== initial
    setIsModified(hasChanged)
    if (hasChanged) closeAlert()
  }, [noteValue, profile])

  const canSave = isModified

  const handleSave = async (e) => {
    e.preventDefault()
    setSavingNote(true)
    try {
      await updateProfileByEmail({ email: userEmail, note: noteValue ?? '' })
      const updatedNote = {
        note: [
          {
            type: 'paragraph',
            children: [{ type: 'text', text: noteValue ?? '' }],
          },
        ],
      }
      onNoteSaved({ note: updatedNote })
      setIsModified(false)
      setIsEditing(false)
      setIsAddingNote(false)
    } catch (err) {
      console.error('Error saving note:', err)
      setAlert({
        show: true,
        variant: 'danger',
        message: 'Unable to save note. Please check the email and try again.',
      })
    } finally {
      setSavingNote(false)
    }
  }

  const handleDeleteNote = () =>
    updateProfileByEmail({ email: userEmail, note: '' }).then(() => {
      onNoteSaved({ note: null })
      setIsEditing(false)
      setIsAddingNote(false)
    })

  const openDeleteModal = () => {
    setIsAnotherModalOpen(true)
    openModal(
      Delete,
      {
        entity: 'note',
        showText: false,
        handleDelete: handleDeleteNote,
        onClose: handleCloseModal,
      },
      { className: 'modal--sm', contentClassName: '' },
      { replace: false }
    )
  }

  const handleCancel = () => {
    setNoteValue(profile ? (notesToPlainText(profile?.note) ?? '') : '')
    setIsEditing(false)
    setIsAddingNote(false)
    if (alert.show) closeAlert()
  }

  return (
    <Card
      body
      className="card--md card--light card-with-border w-75 m-auto my-3"
    >
      <div className="card-body-heading card-body-heading--xs">
        <Card.Title as="h5">Notes on user</Card.Title>
      </div>
      {alert.show && (
        <Alert variant={alert.variant} className="mb-3">
          <p>{alert.message}</p>
        </Alert>
      )}
      {!hasNotes && !isAddingNote ? (
        <>
          <Card body className="card--xs card-with-border mb-3">
            <Card.Text className="info-text info-text-md">
              No notes yet
            </Card.Text>
          </Card>
          <Button
            variant="outline-light"
            size="sm"
            className="btn-plus"
            onClick={() => setIsAddingNote(true)}
          >
            Add note
          </Button>
        </>
      ) : hasNotes && !showForm ? (
        <Stack gap={2}>
          <pre className="m-0">{notesToPlainText(profile?.note)}</pre>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="caption">
              {userSalesRepName && savedAtFormatted
                ? `Saved by ${userSalesRepName} · ${savedAtFormatted}`
                : savedAtFormatted
                  ? `Saved · ${savedAtFormatted}`
                  : ''}
            </span>
            <div className="d-flex gap-2">
              <Button
                variant="outline-light"
                size="sm"
                className="icon-button icon-button-sm btn-edit"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
              <Button
                variant="outline-light"
                size="sm"
                onClick={openDeleteModal}
                className="text-danger"
              >
                Delete
              </Button>
            </div>
          </div>
        </Stack>
      ) : (
        <Form onSubmit={handleSave}>
          <Form.Group className="form-group mb-3" controlId="Notes">
            <Form.Control
              as="textarea"
              rows={6}
              placeholder="Add notes here"
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
            />
          </Form.Group>
          <div className="d-flex justify-content-end gap-2">
            <Button
              type="button"
              variant="outline-light"
              size="lg"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="lg"
              className="btn-width btn-width-md"
              disabled={!canSave || savingNote}
            >
              {savingNote ? <Spinner variant="white" /> : 'Save note'}
            </Button>
          </div>
        </Form>
      )}
    </Card>
  )
}

function getFormInitialValues(
  order,
  profile,
  userOrganizationName,
  userFirstName,
  userLastName,
  userEmail,
  userPhoneNumber,
  userSalesRep
) {
  const mailingAddress = profile?.address || {}
  return {
    organizationName: userOrganizationName,
    employeeId: profile?.employeeId || '',
    firstName: userFirstName,
    lastName: userLastName,
    phoneNumber: userPhoneNumber,
    accountNumber: profile?.accountNumber || '',
    email: userEmail,
    salesRep: userSalesRep,
    address1: mailingAddress?.address_1 || '',
    address2: mailingAddress?.address_2 || '',
    country: mailingAddress?.country || '',
    city: mailingAddress?.city || '',
    state: mailingAddress?.state || '',
    zipCode: mailingAddress?.zipcode || '',
  }
}

function getProfileSaveErrorMessage(err) {
  const apiMessage =
    err?.response?.data?.error?.message || err?.response?.data?.message
  if (apiMessage && /must be unique/i.test(apiMessage)) {
    return 'This email or phone number is already associated with another account.'
  }
  if (apiMessage) return apiMessage
  return 'Unable to save profile. Please check the email and try again.'
}

function UserProfile({
  organization,
  order,
  profile,
  userOrganizationName,
  userFirstName,
  userLastName,
  userEmail,
  userPhoneNumber,
  userSalesRep,
  userSalesRepName,
  tabKey,
  onProfileSaved,
  userTimezone,
}) {
  const [updating, setUpdating] = useState(false)
  const [alert, setAlert] = useState({ show: false, variant: '', message: '' })
  const [salesReps, setSalesReps] = useState([])
  const [isLoadingSalesReps, setIsLoadingSalesReps] = useState(false)
  const prevTabKeyRef = useRef(null)

  // Form state - use profile as fallback when order lacks data
  const [formData, setFormData] = useState(() =>
    getFormInitialValues(
      order,
      profile,
      userOrganizationName,
      userFirstName,
      userLastName,
      userEmail,
      userPhoneNumber,
      userSalesRep
    )
  )

  // Validation state
  const [validationErrors, setValidationErrors] = useState({
    email: false,
    phoneNumber: false,
  })

  // Track if form has been modified
  const [isModified, setIsModified] = useState(false)

  // Reset form when switching to the profile tab or when profile/order loads
  useEffect(() => {
    const justEnteredProfile =
      prevTabKeyRef.current !== 'profile' && tabKey === 'profile'
    prevTabKeyRef.current = tabKey
    if (tabKey === 'profile' && order) {
      setFormData(
        getFormInitialValues(
          order,
          profile,
          userOrganizationName,
          userFirstName,
          userLastName,
          userEmail,
          userPhoneNumber,
          userSalesRep
        )
      )
      if (justEnteredProfile) {
        closeAlert()
        setValidationErrors({ email: false, phoneNumber: false })
      }
    }
  }, [tabKey, order, profile])

  useEffect(() => {
    if (!organization?.uuid) return
    setIsLoadingSalesReps(true)
    getBoxOfficeAccountReps(organization.uuid)
      .then((res) => {
        const reps = res?.data ?? []
        setSalesReps(reps)
      })
      .catch((err) => {
        console.error('Failed to fetch sales reps:', err)
      })
      .finally(() => setIsLoadingSalesReps(false))
  }, [organization?.uuid])

  // Ensure current salesRep from form initial values is in the reps list
  const repOptions = useMemo(() => {
    const initial = getFormInitialValues(
      order,
      profile,
      userOrganizationName,
      userFirstName,
      userLastName,
      userEmail,
      userPhoneNumber,
      userSalesRep
    )
    const currentId = initial.salesRep
    if (!currentId) return salesReps
    const hasCurrent = salesReps.some((r) => String(r.id) === currentId)
    if (hasCurrent) return salesReps
    const label =
      [userSalesRepName].filter(Boolean).join(' ').trim() ||
      `Agent ${currentId}`
    return [
      { id: userSalesRep ?? Number(currentId), label, value: label },
      ...salesReps,
    ]
  }, [salesReps, userSalesRep, userSalesRepName])

  // Check if form data has changed from original (order + profile fallback)
  useEffect(() => {
    const initial = getFormInitialValues(
      order,
      profile,
      userOrganizationName,
      userFirstName,
      userLastName,
      userEmail,
      userPhoneNumber,
      userSalesRep
    )
    const hasChanged = Object.keys(initial).some(
      (key) => formData[key] !== initial[key]
    )
    setIsModified(hasChanged)
    // Only close alert when user modifies form; don't close when order updates after save
    if (hasChanged) closeAlert()
  }, [formData, order, profile])

  // remove email error when email changes
  useEffect(() => {
    setValidationErrors((prev) => ({
      ...prev,
      email: false,
    }))
  }, [formData?.email])

  // remove phoneNumber error when phoneNumber changes
  useEffect(() => {
    setValidationErrors((prev) => ({
      ...prev,
      phoneNumber: false,
    }))
  }, [formData?.phoneNumber])

  const closeAlert = () => {
    setAlert({ show: false, variant: '', message: '' })
  }

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Handle email validation
  const handleValidEmail = (e) => {
    const { value } = e.target
    const isValid = value && emailPatternMatch(value)
    setValidationErrors((prev) => ({
      ...prev,
      email: value && !isValid,
    }))
  }

  // Handle phone number validation
  const handleValidNumber = () => {
    const isValid =
      formData.phoneNumber && isValidPhoneNumber(formData.phoneNumber)
    setValidationErrors((prev) => ({
      ...prev,
      phoneNumber: formData.phoneNumber && !isValid,
    }))
  }

  // Check if form is valid for saving
  const isFormValid = () => {
    const emailValid = !formData.email || emailPatternMatch(formData.email)
    const phoneValid =
      !formData.phoneNumber || isValidPhoneNumber(formData.phoneNumber)
    return emailValid && phoneValid
  }

  // Handle save
  const handleSave = async (e) => {
    e.preventDefault()
    setUpdating(true)

    try {
      if (!formData.email) {
        throw new Error('Email is required to save profile information.')
      }

      const formattedMemberSince = profile?.createdAt
        ? SharedDateTime.formatInTimeZone(
            profile.createdAt,
            SharedDateTime.ensureIanaTimeZone(userTimezone, 'UTC'),
            'yyyy-MM-dd'
          )
        : SharedDateTime.formatInTimeZone(
            new Date(),
            SharedDateTime.ensureIanaTimeZone(userTimezone, 'UTC'),
            'yyyy-MM-dd'
          )

      // Use existing email for lookup (new email (if changed) doesn't exist in DB yet)
      const existingEmail = profile?.email || userEmail || formData.email
      const data = {
        email: existingEmail,
        newEmail: formData.email !== existingEmail ? formData.email : undefined,
        firstName: formData.firstName ?? null,
        lastName: formData.lastName ?? null,
        phoneNumber: formData.phoneNumber ?? null,
        accountNumber: formData.accountNumber ?? null,
        salesRep: formData.salesRep || null,
        organization: formData.organizationName || null,
        employeeId: formData.employeeId || null,
        address1: formData.address1 || null,
        address2: formData.address2 || null,
        city: formData.city || null,
        state: formData.state ?? formData.state ?? null,
        zipCode: formData.zipCode || null,
        country: formData.country || null,
        orderId: order?.id,
        organizationUUID: order?.organizationUUID,
      }
      if (!profile?.memberSince) {
        data.memberSince = formattedMemberSince
      }
      const res = await updateProfileByEmail(data)
      const savedProfile = res?.data ?? {}
      const orderContactOnly = savedProfile.orderContactOnly === true
      setUpdating(false)
      setIsModified(false)
      scrollToTopOfModal()
      setAlert({
        show: true,
        variant: 'success',
        message: orderContactOnly
          ? `${savedProfile.ordersUpdated ?? 0} order(s) updated with the new contact information and moved to the existing ${formData.email} account.`
          : profile
            ? 'Profile updated successfully.'
            : 'Profile created and saved successfully.',
      })
      const rep = formData.salesRep
        ? repOptions.find((r) => String(r.id) === formData.salesRep)
        : null
      const existingAddr =
        profile?.address && typeof profile.address === 'object'
          ? profile.address
          : {}
      const updatedFields = {
        email: formData.email,
        organizationName: formData.organizationName,
        organization: formData.organizationName || null,
        employeeId: formData.employeeId,
        accountNumber: formData.accountNumber,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        memberSince: savedProfile.memberSince ?? formattedMemberSince,
        createdAt: savedProfile.createdAt ?? profile?.createdAt,
        address: {
          ...existingAddr,
          address_1: formData.address1 ?? '',
          address_2: formData.address2 ?? '',
          city: formData.city ?? '',
          state: formData.state ?? '',
          zipcode: formData.zipCode ?? '',
          country: formData.country ?? '',
          latitude: existingAddr?.latitude ?? null,
          longitude: existingAddr?.longitude ?? null,
        },
        sales_rep: formData.salesRep
          ? rep
            ? {
                id: rep.id,
                firstName: rep.label.trim().split(/\s+/)[0] || '',
                lastName:
                  rep.label.trim().split(/\s+/).slice(1).join(' ') || '',
              }
            : { id: Number(formData.salesRep) }
          : null,
      }
      onProfileSaved(
        orderContactOnly
          ? {
              email: formData.email,
              firstName: formData.firstName,
              lastName: formData.lastName,
            }
          : updatedFields
      )
    } catch (err) {
      console.log('err: ', err)
      scrollToTopOfModal()
      setUpdating(false)
      setAlert({
        show: true,
        variant: 'danger',
        message: getProfileSaveErrorMessage(err),
      })
    }
  }

  const canSave = isModified && isFormValid()

  return (
    <Card
      body
      className="card--md card--light card-with-border w-75 m-auto my-3"
    >
      <div className="card-body-heading card-body-heading--xs">
        <Card.Title as="h5">Profile Information</Card.Title>
      </div>
      {alert.show && (
        <>
          <Alert variant={alert.variant} className="mb-3">
            <p>{alert.message}</p>
          </Alert>
        </>
      )}
      {isLoadingSalesReps ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner variant="dark" />
        </div>
      ) : (
        <Form onSubmit={handleSave}>
          <Row>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="Sales Rep">
                <Form.Label>Sales Rep</Form.Label>
                <Form.Select
                  value={formData.salesRep}
                  onChange={(e) =>
                    handleInputChange('salesRep', e.target.value)
                  }
                  autoFocus
                >
                  <option hidden disabled value="">
                    Select sales rep
                  </option>
                  {repOptions.map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {capitalizeString(rep.label)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group
                className="form-group mb-3"
                controlId="organizationName"
              >
                <Form.Label>Organization</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) =>
                    handleInputChange('organizationName', e.target.value)
                  }
                  placeholder="Enter organization name"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="employeeId">
                <Form.Label>Employee ID</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.employeeId}
                  onChange={(e) =>
                    handleInputChange('employeeId', e.target.value)
                  }
                  placeholder="Enter employee ID"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="accountNumber">
                <Form.Label>Account Number</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) =>
                    handleInputChange('accountNumber', e.target.value)
                  }
                  placeholder="Enter account number"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="firstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  pattern={namePatternMatch}
                  value={formData.firstName}
                  required
                  onChange={(e) =>
                    handleInputChange(
                      'firstName',
                      e.target.validity.valid || e.target.value === ''
                        ? e.target.value
                        : formData.firstName
                    )
                  }
                  placeholder="Enter first name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="lastName">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  pattern={namePatternMatch}
                  required
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange(
                      'lastName',
                      e.target.validity.valid || e.target.value === ''
                        ? e.target.value
                        : formData.lastName
                    )
                  }
                  placeholder="Enter last name"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email address"
                  className={validationErrors.email ? 'border-danger' : ''}
                  onBlur={handleValidEmail}
                  required
                />
                {validationErrors.email && <Error type="invalidEmail" />}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group
                className="form-group mt-0 mb-3"
                controlId="phoneNumber"
              >
                <Form.Label>Phone Number</Form.Label>
                <PhoneNumberInput
                  phoneNumber={formData.phoneNumber}
                  setPhoneNumber={(value) =>
                    handleInputChange('phoneNumber', value)
                  }
                  onBlur={handleValidNumber}
                  hasError={validationErrors.phoneNumber}
                  isRequired={false}
                />
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />
          <div className="card-body-heading card-body-heading--xs mb-3">
            <Card.Title as="h6" className="mb-0">
              Mailing Address
            </Card.Title>
          </div>
          <Row>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="address1">
                <Form.Label>Address 1</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.address1}
                  onChange={(e) =>
                    handleInputChange('address1', e.target.value)
                  }
                  placeholder="Enter address line 1"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="address2">
                <Form.Label>Address 2</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.address2}
                  onChange={(e) =>
                    handleInputChange('address2', e.target.value)
                  }
                  placeholder="Enter address line 2"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="country">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  placeholder="Enter country"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="city">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Enter city"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="state">
                <Form.Label>State/Province</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="Enter state or province"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="form-group mb-3" controlId="zipCode">
                <Form.Label>Zip Code</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  placeholder="Enter zip code"
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="flex justify-content-end">
            <Button
              type="submit"
              size="lg"
              className="btn-width btn-width-md"
              disabled={!canSave || updating}
            >
              {updating ? <Spinner variant="white" /> : 'Update Profile'}
            </Button>
          </div>
        </Form>
      )}
    </Card>
  )
}

function PaymentMethods({
  connectAccountId,
  stripeCustomerId,
  savedCards,
  reloadPaymentMethods,
  preventLastPaymentMethod = false,
  openModal,
  closeModal,
  handleCloseModal,
  setIsAnotherModalOpen,
}) {
  const {
    processingPaymentMethodId,
    paymentMethodsError,
    handleSetDefaultPaymentMethod,
    openReplacePaymentMethodModal,
    openDeletePaymentMethodModal,
  } = useCustomerPaymentMethods({
    stripeCustomerId,
    connectAccountId,
    preventLastPaymentMethod,
    deleteConfirmText: preventLastPaymentMethod
      ? 'This card will no longer be saved for future charges. Customers with an active payment plan must keep at least one saved card.'
      : 'This card will no longer be saved for future charges.',
    openModal: (...args) => {
      setIsAnotherModalOpen(true)
      return openModal(...args)
    },
    closeModal: handleCloseModal || closeModal,
    onReload: reloadPaymentMethods,
  })

  const openReplaceModal = (paymentMethodId) => {
    setIsAnotherModalOpen(true)
    openReplacePaymentMethodModal(paymentMethodId)
  }

  const openDeleteModal = (paymentMethodId) => {
    setIsAnotherModalOpen(true)
    openDeletePaymentMethodModal(paymentMethodId)
  }

  return (
    <Card
      body
      className="card--md card--light card-with-border w-75 m-auto my-3"
    >
      <Stack gap={3}>
        <div>
          <Card.Title as="h5">Payment methods</Card.Title>
          <Card.Subtitle as="h6" className="subtitle--dark mt-2">
            Update cards saved to this Stripe customer for future purchases.
          </Card.Subtitle>
        </div>

        {paymentMethodsError ? (
          <Alert variant="danger" className="mb-0">
            {paymentMethodsError}
          </Alert>
        ) : null}

        {!stripeCustomerId ? (
          <Card body className="card--xs info-card card-with-border mb-0">
            <Card.Text className="info-text info-text-md">
              There are no saved cards for this user. Cards will be
              automatically stored here after completing a successful
              transaction.
            </Card.Text>
          </Card>
        ) : savedCards?.length > 0 ? (
          <CustomerPaymentMethodsTable
            savedPaymentMethods={savedCards}
            processingPaymentMethodId={processingPaymentMethodId}
            preventLastPaymentMethod={preventLastPaymentMethod}
            onSetDefault={handleSetDefaultPaymentMethod}
            onUpdateCard={openReplaceModal}
            onRemoveCard={openDeleteModal}
          />
        ) : (
          <Card body className="card--xs info-card card-with-border mb-0">
            <Card.Text className="info-text info-text-md">
              No saved cards were returned for this customer.
            </Card.Text>
          </Card>
        )}
      </Stack>
    </Card>
  )
}
